/**
 * Cross-compatible PPTX text extraction.
 *
 * Validates the ZIP archive at the binary level (archive size, entry count,
 * slide count, presentation manifest, per-entry size caps, compression-ratio
 * bomb checks) then delegates text extraction to `officeparser`, which handles
 * OOXML namespace routing, table-cell text, and speaker notes.
 *
 * The module has no client- or server-only imports so browser uploads and the
 * asynchronous onboarding parser use the same extraction behavior. officeparser
 * is loaded via dynamic import() to code-split it out of the main bundle (it
 * has an ESM-only transitive dep on file-type; its own CJS entry handles this
 * via dynamic import(), and the browser export swaps in a self-contained
 * build — but a static top-level import would eagerly bundle it on every page
 * that touches pptx.ts).
 *
 * Known divergences from the pre-officeparser scanner (both stem from
 * officeparser reading slideN.xml files directly instead of walking
 * presentation.xml's sldIdLst): slides are emitted in FILE-NUMBER order, not
 * display order, so decks whose slides were reordered after creation extract
 * in creation order; and orphaned slide parts (present on disk but removed
 * from sldIdLst) are extracted and counted rather than skipped.
 */
import type { OfficeContentNode } from "officeparser";

// Thrown when a PPTX yields no text or no slides; re-exported so existing importers keep working.
import { PPTX_NO_TEXT_ERROR, PPTX_NO_SLIDES_ERROR } from "@/constants/deckParse";
export { PPTX_NO_TEXT_ERROR, PPTX_NO_SLIDES_ERROR } from "@/constants/deckParse";


// ---------------------------------------------------------------------------
// Safety constants — applied BEFORE officeparser touches the buffer
// ---------------------------------------------------------------------------

const MAX_ARCHIVE_ENTRIES = 5_000;
const MAX_SLIDES = 200;
const MAX_SLIDE_XML_BYTES = 2 * 1024 * 1024;
const MAX_TOTAL_SLIDE_XML_BYTES = 10 * 1024 * 1024;
// Cap on the archive's DECLARED total uncompressed size, summed over every
// entry in the pre-scan (officeparser inflates layouts/masters/media too, not
// just slide XML, so this must sit well above MAX_ARCHIVE_BYTES: XML
// compresses ~10x, media ~1x). officeparser's actual-bytes limit is then
// derived from the declared total (plus slack) rather than this constant —
// see extractPptxText — so lying headers abort at their declared size, not
// at this ceiling (PR #689 review, P1).
const MAX_ARCHIVE_DECLARED_UNCOMPRESSED_BYTES = 128 * 1024 * 1024;
// Slack between declared and enforced actual bytes: honest archives inflate
// to exactly their declared sizes, so anything past declared + slack is a
// lying header and aborting is the point.
const UNCOMPRESSED_BYTES_SLACK = 1024 * 1024;
const MAX_EXTRACTED_TEXT_CHARS = 200_000;
const MAX_COMPRESSION_RATIO = 200;
const MAX_ARCHIVE_BYTES = 50 * 1024 * 1024;
const ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
const ZIP_END_OF_CENTRAL_DIRECTORY_MIN_BYTES = 22;
const ZIP_MAX_COMMENT_BYTES = 65_535;
const ZIP_CENTRAL_DIRECTORY_SIGNATURE = 0x02014b50;
const ZIP_CENTRAL_DIRECTORY_HEADER_BYTES = 46;

const PPTX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";

// ---------------------------------------------------------------------------
// Text sanitization
// ---------------------------------------------------------------------------

/**
 * Remove bytes and invisible characters that cannot safely be persisted.
 */
export function sanitizePptxText(text: string): string {
  let sanitized = text.replace(/\0/g, "");
  sanitized = sanitized.replace(/[\u200B-\u200D\uFEFF]/g, "");
  sanitized = sanitized.replace(/[\x01-\x08\x0B-\x0C\x0E-\x1F]/g, "");

  try {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder("utf-8", { fatal: false });
    sanitized = decoder.decode(encoder.encode(sanitized));
  } catch { /* benign: TextEncoder/TextDecoder unavailable in edge runtimes */
    sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F]/g, "");
  }

  return sanitized
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface PptxTextExtraction {
  text: string;
  slideCount: number;
  /**
   * Per-slide text, with the slide's position in the deck.
   *
   * `text` above is these joined, and it is what the summarising callers
   * want. This is what a REBUILD wants: the words that were on each slide,
   * still attached to the slide they were on. Deriving it back out of the
   * joined blob is impossible once a slide's own text contains a blank line.
   *
   * KNOWN GAP: officeparser omits slides with no extractable text (blank or
   * image-only), so those slides are absent here and the numbering counts
   * only text-bearing slides. `slideCount` above still reports every slide in
   * the archive, so the two deliberately disagree for such decks. A rebuild
   * therefore reproduces every slide that said something and silently drops
   * a full-bleed image slide's position (LEV-1410).
   */
  slides: { slideNumber: number; text: string }[];
}

// ---------------------------------------------------------------------------
// Binary archive safety validation
// ---------------------------------------------------------------------------

interface ArchiveSafetyResult {
  bytes: Uint8Array;
  /** Slide XML entries referenced by the archive (blank slides included). */
  slideEntryCount: number;
  /** Sum of every entry's declared uncompressed size (central directory). */
  totalDeclaredUncompressedBytes: number;
}

/**
 * Validate archive structure at the binary level before any library touches
 * the data. Checks: total archive size, central-directory integrity,
 * entry count, slide count, presentation-manifest presence, per-entry
 * uncompressed/compressed sizes, and compression ratio.
 *
 * The size/ratio checks read the central directory's DECLARED sizes, which a
 * crafted archive can lie about — their job is to reject garbage cheaply
 * before the officeparser chunk is even loaded. The authoritative
 * decompression defense is officeparser's `decompressionLimits`, which caps
 * ACTUAL inflated bytes while streaming.
 *
 * Returns the Uint8Array view plus the slide-entry count on success; throws
 * on any violation.
 */
export function assertArchiveSafety(data: ArrayBuffer | Uint8Array): ArchiveSafetyResult {
  const bytes =
    data instanceof Uint8Array
      ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
      : new Uint8Array(data);
  if (bytes.byteLength > MAX_ARCHIVE_BYTES) {
    throw new Error("PPTX archive exceeds the upload extraction limit");
  }
  if (bytes.byteLength < ZIP_END_OF_CENTRAL_DIRECTORY_MIN_BYTES) {
    throw new Error("Uploaded file is not a valid PPTX archive");
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const earliestOffset = Math.max(
    0,
    bytes.byteLength -
      ZIP_END_OF_CENTRAL_DIRECTORY_MIN_BYTES -
      ZIP_MAX_COMMENT_BYTES,
  );
  let recordOffset = -1;
  for (
    let offset = bytes.byteLength - ZIP_END_OF_CENTRAL_DIRECTORY_MIN_BYTES;
    offset >= earliestOffset;
    offset -= 1
  ) {
    if (
      view.getUint32(offset, true) ===
      ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE
    ) {
      recordOffset = offset;
      break;
    }
  }
  if (recordOffset < 0) {
    throw new Error("Uploaded file is not a valid PPTX archive");
  }

  const diskNumber = view.getUint16(recordOffset + 4, true);
  const centralDirectoryDisk = view.getUint16(recordOffset + 6, true);
  const totalEntries = view.getUint16(recordOffset + 10, true);
  const centralDirectoryBytes = view.getUint32(recordOffset + 12, true);
  const centralDirectoryOffset = view.getUint32(recordOffset + 16, true);
  const commentBytes = view.getUint16(recordOffset + 20, true);
  if (
    diskNumber !== 0 ||
    centralDirectoryDisk !== 0 ||
    totalEntries === 0xffff
  ) {
    throw new Error("PPTX archive uses an unsupported ZIP structure");
  }
  if (totalEntries > MAX_ARCHIVE_ENTRIES) {
    throw new Error("PPTX archive contains too many entries");
  }
  if (
    recordOffset +
      ZIP_END_OF_CENTRAL_DIRECTORY_MIN_BYTES +
      commentBytes !==
      bytes.byteLength ||
    centralDirectoryOffset + centralDirectoryBytes !== recordOffset
  ) {
    throw new Error("Uploaded file has an invalid PPTX central directory");
  }

  const centralDirectoryEnd =
    centralDirectoryOffset + centralDirectoryBytes;
  let centralCursor = centralDirectoryOffset;
  let actualEntries = 0;
  let slideEntryCount = 0;
  let hasPresentationManifest = false;
  let totalUncompressedSlideBytes = 0;
  let totalDeclaredUncompressedBytes = 0;
  const decoder = new TextDecoder("utf-8", { fatal: false });

  while (centralCursor < centralDirectoryEnd) {
    if (
      centralCursor + ZIP_CENTRAL_DIRECTORY_HEADER_BYTES >
        centralDirectoryEnd ||
      view.getUint32(centralCursor, true) !==
        ZIP_CENTRAL_DIRECTORY_SIGNATURE
    ) {
      throw new Error("Uploaded file has an invalid PPTX central directory");
    }

    const compressedSize = view.getUint32(centralCursor + 20, true);
    const uncompressedSize = view.getUint32(centralCursor + 24, true);
    const fileNameBytes = view.getUint16(centralCursor + 28, true);
    const extraFieldBytes = view.getUint16(centralCursor + 30, true);
    const fileCommentBytes = view.getUint16(centralCursor + 32, true);

    // Read entry name to identify slide XML files
    const nameStart = centralCursor + ZIP_CENTRAL_DIRECTORY_HEADER_BYTES;
    const entryName = decoder.decode(
      bytes.subarray(nameStart, nameStart + fileNameBytes),
    );
    const isSlideXml = /^ppt\/slides\/slide\d+\.xml$/i.test(entryName);
    const isNotesXml = /^ppt\/notesSlides\/notesSlide\d+\.xml$/i.test(entryName);
    if (/^ppt\/presentation\.xml$/i.test(entryName)) {
      hasPresentationManifest = true;
    }

    if (isSlideXml) {
      slideEntryCount += 1;
      if (slideEntryCount > MAX_SLIDES) {
        throw new Error("PPTX slide count exceeds the extraction limit");
      }
    }

    totalDeclaredUncompressedBytes += uncompressedSize;
    if (totalDeclaredUncompressedBytes > MAX_ARCHIVE_DECLARED_UNCOMPRESSED_BYTES) {
      throw new Error("PPTX archive exceeds the total extraction limit");
    }

    // Per-entry safety: compression ratio and size caps
    if (isSlideXml || isNotesXml) {
      if (uncompressedSize > MAX_SLIDE_XML_BYTES) {
        throw new Error(`${entryName} exceeds the PPTX extraction limit`);
      }
      if (
        uncompressedSize > 0 &&
        (compressedSize === 0 ||
          uncompressedSize / compressedSize > MAX_COMPRESSION_RATIO)
      ) {
        throw new Error(`${entryName} has a suspicious compression ratio`);
      }
      totalUncompressedSlideBytes += uncompressedSize;
      if (totalUncompressedSlideBytes > MAX_TOTAL_SLIDE_XML_BYTES) {
        throw new Error("PPTX slide XML exceeds the total extraction limit");
      }
    }

    centralCursor +=
      ZIP_CENTRAL_DIRECTORY_HEADER_BYTES +
      fileNameBytes +
      extraFieldBytes +
      fileCommentBytes;
    actualEntries += 1;
    if (actualEntries > MAX_ARCHIVE_ENTRIES) {
      throw new Error("PPTX archive contains too many entries");
    }
  }
  if (centralCursor !== centralDirectoryEnd || actualEntries !== totalEntries) {
    throw new Error("Uploaded file has an invalid PPTX central directory");
  }
  if (!hasPresentationManifest) {
    // Renamed-DOCX/XLSX defense: an OOXML archive without ppt/presentation.xml
    // is not a presentation, whatever its extension claims.
    throw new Error(
      "Uploaded file is not a valid PPTX archive (missing presentation manifest)",
    );
  }
  return { bytes, slideEntryCount, totalDeclaredUncompressedBytes };
}

// ---------------------------------------------------------------------------
// officeparser text-node walker
// ---------------------------------------------------------------------------

/**
 * Collect all text from an officeparser content node.
 *
 * officeparser's contract (verified against 7.8.0's PowerPointParser): a node
 * that has `text` (paragraph, cell) already carries the FULL concatenation of
 * its descendant runs — `<a:br>` breaks included — while its `children`
 * duplicate those same runs as leaf nodes. So `text` wins when present, and
 * recursion is only for container nodes (slide, table, row) that have no
 * aggregate of their own. Reading both, as a naive walker would, emits every
 * paragraph twice.
 *
 * Exception: `cell` aggregates its paragraphs with NO separator ("Q1
 * Revenue$1.2M"), so cells recurse into their paragraph children instead,
 * which restores the newline joins.
 */
function collectNodeText(node: OfficeContentNode): string {
  if (node.text && !(node.type === "cell" && node.children?.length)) {
    return node.text;
  }
  if (!node.children?.length) return "";
  const parts: string[] = [];
  for (const child of node.children) {
    const childText = collectNodeText(child);
    if (childText) parts.push(childText);
  }
  return parts.join("\n");
}

// ---------------------------------------------------------------------------
// Main extraction
// ---------------------------------------------------------------------------

/**
 * Extract readable text from a validated PPTX package, slides in file-number
 * order (see the module docblock for the display-order divergence).
 *
 * Safety guards run at the binary level BEFORE officeparser touches the data:
 * archive size cap, entry count cap, slide count cap, presentation-manifest
 * check, per-slide XML size cap, total XML cap, compression-ratio zip-bomb
 * check. The total extracted char cap is applied after extraction.
 */
export async function extractPptxText(
  data: ArrayBuffer | Uint8Array,
): Promise<PptxTextExtraction> {
  // --- Binary safety validation (before any library touches the data) ---
  const { bytes, slideEntryCount, totalDeclaredUncompressedBytes } =
    assertArchiveSafety(data);
  if (slideEntryCount === 0) {
    throw new Error(PPTX_NO_SLIDES_ERROR);
  }

  // --- Parse with officeparser ---
  // Dynamic import: officeparser has an ESM-only transitive dep (file-type);
  // its CJS entry handles this internally via dynamic import(), but a static
  // top-level import would eagerly bundle the full parser on every page that
  // imports pptx.ts.
  const { parseOffice } = await import("officeparser");

  // parseOffice accepts Uint8Array directly — no Buffer wrap, which would
  // break in the browser (this module also serves client-side uploads).
  const ast = await parseOffice(bytes, {
    fileType: "pptx",
    ignoreNotes: false,
    ignoreSlideMasters: true,
    extractAttachments: false,
    ocr: false,
    includeRawContent: false,
    decompressionLimits: {
      // Actual-bytes limit derived from the pre-scan's declared total: honest
      // archives inflate to exactly their declared sizes, so a crafted deck
      // whose headers under-declare aborts here at declared + slack instead
      // of a fixed multi-hundred-MB ceiling (PR #689 review, P1).
      maxUncompressedBytes:
        totalDeclaredUncompressedBytes + UNCOMPRESSED_BYTES_SLACK,
      maxZipEntries: MAX_ARCHIVE_ENTRIES,
    },
  });

  // officeparser omits slides with no extractable children (blank or
  // image-only), so this list can be shorter than slideEntryCount.
  const slides = ast.content.filter((node) => node.type === "slide");

  const slideTexts: string[] = [];
  // Parallel to slideTexts but keeps its own numbering: slideTexts skips
  // slides that extract to nothing and `text` below depends on it continuing
  // to, so the per-slide record is built alongside rather than derived.
  const slideRecords: { slideNumber: number; text: string }[] = [];
  let extractedCharacters = 0;
  let slideNumber = 0;

  for (const slide of slides) {
    slideNumber += 1;
    const bodyText = collectNodeText(slide);
    const notesText = (slide.notes ?? [])
      .map(collectNodeText)
      .filter(Boolean)
      .join("\n");
    const combined = [bodyText, notesText].filter(Boolean).join("\n");

    if (combined) {
      slideTexts.push(combined);
      slideRecords.push({ slideNumber, text: combined });
      extractedCharacters += combined.length;
      if (extractedCharacters > MAX_EXTRACTED_TEXT_CHARS) {
        throw new Error("PPTX extracted text exceeds the synthesis limit");
      }
    }
  }

  const text = sanitizePptxText(slideTexts.join("\n\n"));
  if (!text) {
    throw new Error(PPTX_NO_TEXT_ERROR);
  }

  // slideCount reports every slide the archive references (from the central
  // directory), not just the ones officeparser kept — parity with the old
  // manifest-based count, so blank/divider slides still count.
  return { text, slideCount: slideEntryCount, slides: slideRecords };
}

// ---------------------------------------------------------------------------
// Client-side File wrapper
// ---------------------------------------------------------------------------

/**
 * Parse a PPTX file to text using client-side extraction.
 * Throws if the file is not a PPTX, parsing fails, or no text is extracted.
 */
export async function parsePptxToText(file: File): Promise<string> {
  if (file.type !== PPTX_MIME_TYPE && !file.name.toLowerCase().endsWith(".pptx")) {
    throw new Error("File must be a PPTX");
  }

  const buffer = await file.arrayBuffer();
  return (await extractPptxText(buffer)).text;
}
